import React, { createContext, useState, useCallback, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { canvasConfig } from '../config/awsConfig';
import {
  MOCK_COURSES,
  MOCK_COURSE_DETAILS,
  MOCK_USER,
  DEMO_MODE_KEY,
  simulateApiDelay,
} from '../data/mockData';
import { adaptSnapshot, selectNearestSnapshot } from '../utils/snapshotAdapter';
import { getSnapshots, getSnapshot } from '../services/snapshotService';
import {
  OFFLINE_MODE_KEY,
  OFFLINE_SNAPSHOT_KEY,
  OFFLINE_USER_KEY,
  OFFLINE_OWNER_KEY,
  OFFLINE_DISCARDED_EVENT,
} from '../utils/offlineSession';

// Task 6.0e. Kept in sessionStorage alongside DEMO_MODE_KEY so a page reload
// does not drop a token-less user back into the broken authenticated view
// they entered offline mode to escape.
// Task 6.0ag: the key names live in utils/offlineSession so AuthContext's
// sign-out carve-out and Login's same-account check spell them identically.

const readStoredOfflineSnapshot = (): any => {
  try {
    const raw = sessionStorage.getItem(OFFLINE_SNAPSHOT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('Error parsing stored offline snapshot:', error);
    sessionStorage.removeItem(OFFLINE_SNAPSHOT_KEY);
    return null;
  }
};

// Task 4.5: split out of CanvasDataContext.tsx. This context owns the app's
// MODE (guest/demo/authenticated) and the credentials each mode implies --
// `getActiveCredentials()` is the sanctioned abstraction other code should
// call instead of branching on mode directly to pick apart a
// token/userId/rootUrl.
//
// The three-way mode is stored as one `coursesMode` enum rather than two
// independent booleans -- the pre-4.5 file tracked each mode flag as a
// separate useState call, which allowed (in principle) contradictory
// combinations no code path actually produced. The two boolean fields on
// the public interface below are cheap derived values off the single
// source of truth, kept for every existing consumer that reads them by name.
//
// `pendingUserSelection` and `initializing` live here rather than in
// CanvasSessionContext even though they read as "session" state: this
// context's `getActiveCredentials()` gates on pendingUserSelection (an
// identity isn't "active" until a user is chosen), and AppModeContext is
// deliberately a leaf that only depends on AuthContext -- not on
// CanvasSessionContext -- so that dependency has to run this direction.
// CanvasSessionContext depends on this context, not the other way around.
//
// Demo mode's mock course data (demoCourses/demoCourseDetails) is owned
// here too, alongside guestCredentials, on the theory that "the data a mode
// implies" is the same kind of thing whether it comes from a guest token or
// a mock fixture -- CanvasDataContext (the data-fetching layer) reads both
// through this context rather than owning a demo-only copy itself.

// Task 6.0e adds 'offline': the app runs off a stored snapshot instead of
// live Canvas calls. It is deliberately NOT folded into 'demo' -- demo means
// "this is fabricated sample data" and carries useDemoToRealTransition
// behaviour, whereas offline data is the user's own real data that simply
// is not live. Conflating them would show a "you're viewing sample data"
// affordance over a real child's grades.
//
// "Offline" means offline from *Canvas*, not from the network: this mode
// still calls the application's own API to read snapshots. Genuine
// no-network operation is the service worker's job (task 5.3a).
type Mode = 'demo' | 'guest' | 'auth' | 'offline';

interface AppModeContextType {
  isGuestMode: boolean;
  isDemoMode: boolean;
  isOfflineMode: boolean;
  guestCredentials: any;
  guestAvailableUsers: any[];
  pendingUserSelection: boolean;
  initializing: boolean;
  coursesMode: Mode;
  dashboardPath: string;
  demoCourses: any[];
  demoCourseDetails: Record<string | number, any>;
  offlineCourses: any[];
  offlineCourseDetails: Record<string | number, any>;
  offlineSnapshot: any;
  offlineCanvasUserId: string | null;
  getActiveCredentials: () => any;
  setGuestCredentials: (token: string, userId: string | number | null, rootUrl: string, users?: any[]) => void;
  clearGuestCredentials: () => void;
  resetGuestSelection: () => void;
  activateDemoMode: () => Promise<boolean>;
  deactivateDemoMode: () => void;
  activateOfflineMode: (canvasUserId: string, snapshot: any) => boolean;
  deactivateOfflineMode: () => void;
  switchOfflineChild: (canvasUserId: string) => Promise<OfflineSwitchResult>;
}

/** Task 6.0s. What switching children in offline mode produced. */
export interface OfflineSwitchResult {
  ok: boolean;
  /** The snapshot now on screen, when `ok`. */
  snapshot?: any;
  /** Why not, when not `ok`. Phrased for the sidebar to show as-is. */
  reason?: string;
}

const AppModeContext = createContext<AppModeContextType | undefined>(undefined);

export const useAppMode = (): AppModeContextType => {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
};

interface AppModeProviderProps {
  children: ReactNode;
}

export const AppModeProvider: React.FC<AppModeProviderProps> = ({ children }) => {
  const { currentUser, availableUsers, switchCanvasUser } = useAuth();

  const [pendingUserSelection, setPendingUserSelection] = useState(() => {
    if (sessionStorage.getItem(DEMO_MODE_KEY) === 'true') {
      return false; // No user selection needed in demo mode
    }
    return true;
  });
  const [initializing, setInitializing] = useState(true);

  const [guestAvailableUsers, setGuestAvailableUsers] = useState<any[]>([]);
  const [guestCredentials, setGuestCredentialsState] = useState<any>(() => {
    try {
      const savedCredentials = sessionStorage.getItem('guestCredentials');
      return savedCredentials ? JSON.parse(savedCredentials) : null;
    } catch (error) {
      console.error('Error parsing guest credentials:', error);
      sessionStorage.removeItem('guestCredentials');
      return null;
    }
  });

  const [coursesMode, setCoursesMode] = useState<Mode>(() => {
    if (sessionStorage.getItem(DEMO_MODE_KEY) === 'true') return 'demo';
    if (sessionStorage.getItem(OFFLINE_MODE_KEY) === 'true') return 'offline';
    if (sessionStorage.getItem('isGuestMode')) return 'guest';
    return 'auth';
  });
  const isGuestMode = coursesMode === 'guest';
  const isDemoMode = coursesMode === 'demo';
  const isOfflineMode = coursesMode === 'offline';

  // Demo mode's mock data. Only demo reads these -- guest/auth courses come
  // from CanvasDataContext's TanStack Query cache instead.
  const [demoCourses, setDemoCourses] = useState<any[]>(() => (coursesMode === 'demo' ? MOCK_COURSES : []));
  const [demoCourseDetails, setDemoCourseDetails] = useState<Record<string | number, any>>(() =>
    (coursesMode === 'demo' ? MOCK_COURSE_DETAILS : {})
  );

  // Task 6.0e: the snapshot being viewed, and the courses/courseDetails
  // adapted out of it. Held here for the same reason demo's mock data is --
  // "the data a mode implies" belongs with the mode, and CanvasDataContext
  // reads it through this context rather than owning a copy.
  const [offlineSnapshot, setOfflineSnapshot] = useState<any>(() =>
    (coursesMode === 'offline' ? readStoredOfflineSnapshot() : null)
  );
  const [offlineCanvasUserId, setOfflineCanvasUserId] = useState<string | null>(() =>
    (coursesMode === 'offline' ? sessionStorage.getItem(OFFLINE_USER_KEY) : null)
  );

  // Derived, not stored: keeping the adapted form in state as well as the
  // snapshot would let the two disagree after a snapshot swap.
  const { courses: offlineCourses, courseDetails: offlineCourseDetails } = useMemo(
    () => adaptSnapshot(offlineSnapshot),
    [offlineSnapshot]
  );

  // Determine whether a user selection is still pending on first load.
  useEffect(() => {
    const initializeMode = async () => {
      try {
        const savedCredentials = sessionStorage.getItem('guestCredentials');
        if (savedCredentials) {
          const credentials = JSON.parse(savedCredentials);
          setGuestCredentialsState(credentials);
          setCoursesMode('guest');

          if (credentials.availableUsers && credentials.availableUsers.length > 0) {
            setGuestAvailableUsers(credentials.availableUsers);

            if (!credentials.userId && Array.isArray(credentials.availableUsers) && credentials.availableUsers.length > 0) {
              const firstUser = credentials.availableUsers[0];
              credentials.userId = firstUser.id.toString();
              credentials.selectedUser = firstUser;

              sessionStorage.setItem('guestCredentials', JSON.stringify(credentials));
              setGuestCredentialsState(credentials);
            }

            setPendingUserSelection(false);
          } else {
            setPendingUserSelection(false);
          }
          return;
        }

        if (currentUser?.canvasToken) {
          if (currentUser.canvasUserId && Array.isArray(availableUsers) && availableUsers.length > 0) {
            setPendingUserSelection(false);
          } else if (Array.isArray(availableUsers) && availableUsers.length > 0) {
            const firstUser = availableUsers[0];
            await switchCanvasUser(firstUser.id.toString());
            setPendingUserSelection(false);
          } else {
            setPendingUserSelection(true);
          }
        } else {
          setPendingUserSelection(false);
        }
      } catch (error) {
        console.error('Error initializing app mode:', error);
        sessionStorage.removeItem('guestCredentials');
        sessionStorage.removeItem('isGuestMode');
        setGuestCredentialsState(null);
        // Only guest state is touched above -- leave demo mode as-is.
        setCoursesMode(prev => (prev === 'demo' ? 'demo' : 'auth'));
      } finally {
        setInitializing(false);
      }
    };

    initializeMode();
  }, [currentUser, availableUsers, switchCanvasUser]);

  // Deactivate demo mode when the user authenticates, keeping React state in
  // sync when sessionStorage is cleared on login.
  useEffect(() => {
    if (currentUser?.canvasToken && coursesMode === 'demo') {
      console.log('🔄 User authenticated while in demo mode - deactivating demo mode');
      setCoursesMode('auth');
      setDemoCourses([]);
      setDemoCourseDetails({});
      setPendingUserSelection(false);
    }
  }, [currentUser, coursesMode]);

  const getActiveCredentials = useCallback(() => {
    // CRITICAL: Always return null in demo mode to force use of mock data,
    // even if a real Canvas token is also present.
    if (coursesMode === 'demo') {
      return null;
    }

    // Task 6.0e: same reasoning as demo, and more load-bearing here. A stale
    // Canvas token may well still be stored -- it is the token's failure that
    // sent the user into offline mode -- and returning it would send every
    // consumer straight back to the live fetches this mode exists to avoid.
    if (coursesMode === 'offline') {
      return null;
    }

    if (pendingUserSelection) {
      return null;
    }

    if (coursesMode === 'guest' && guestCredentials) {
      return {
        token: guestCredentials.token,
        userId: guestCredentials.userId,
        rootUrl: guestCredentials.rootUrl || canvasConfig.defaultRootUrl,
        selectedUser: guestCredentials.selectedUser || null,
      };
    }

    if (currentUser?.canvasToken && currentUser?.canvasUserId) {
      return {
        token: currentUser.canvasToken,
        userId: currentUser.canvasUserId,
        rootUrl: currentUser.canvasRootUrl || canvasConfig.defaultRootUrl,
        selectedUser: null,
      };
    }

    return null;
  }, [currentUser, guestCredentials, coursesMode, pendingUserSelection]);

  // Consolidates a ternary that used to be re-derived independently in half
  // a dozen components (Header, Sidebar, CourseDetails...): "where does the
  // back/dashboard link for the current identity point".
  const dashboardPath = coursesMode === 'guest' ? '/guest/dashboard' : '/dashboard';

  const setGuestCredentials = useCallback((token: string, userId: string | number | null, rootUrl: string, users: any[] = []) => {
    const credentials: any = {
      token,
      userId,
      rootUrl: rootUrl || canvasConfig.defaultRootUrl,
      availableUsers: users.length > 0 ? users : [],
      selectedUser: null,
    };

    if (userId && users.length > 0) {
      const selectedUser = users.find(user => user.id.toString() === userId.toString());
      if (selectedUser) {
        credentials.selectedUser = selectedUser;
        credentials.userId = userId.toString();
      }
    }

    setGuestCredentialsState(credentials);

    if (users && users.length > 0) {
      setGuestAvailableUsers(users);

      if (!userId && users.length > 0) {
        const firstUser = users[0];
        credentials.userId = firstUser.id.toString();
        credentials.selectedUser = firstUser;
        setGuestCredentialsState(credentials);
        sessionStorage.setItem('guestCredentials', JSON.stringify(credentials));
      }

      setPendingUserSelection(false);
    } else {
      setGuestAvailableUsers([]);
      setPendingUserSelection(true);
    }

    sessionStorage.setItem('guestCredentials', JSON.stringify(credentials));
    sessionStorage.setItem('isGuestMode', 'true');
    setCoursesMode('guest');
  }, []);

  // Exit guest mode. Matches the original CanvasDataContext behaviour: does
  // NOT touch guestAvailableUsers or pendingUserSelection (that's
  // resetGuestSelection's job, used for a full logout reset).
  const clearGuestCredentials = useCallback(() => {
    setGuestCredentialsState(null);
    setCoursesMode('auth');
    sessionStorage.removeItem('guestCredentials');
    sessionStorage.removeItem('isGuestMode');
  }, []);

  // Broader reset used on logout: also drops the available-users list and
  // puts user selection back into a pending state.
  const resetGuestSelection = useCallback(() => {
    setGuestCredentialsState(null);
    setGuestAvailableUsers([]);
    setCoursesMode('auth');
    setPendingUserSelection(true);
    sessionStorage.removeItem('guestCredentials');
    sessionStorage.removeItem('isGuestMode');
  }, []);

  const activateDemoMode = useCallback(async () => {
    console.log('🎮 Activating demo mode...');

    try {
      // Simulate API delay for realistic experience.
      await simulateApiDelay(400, 800);

      sessionStorage.setItem(DEMO_MODE_KEY, 'true');
      sessionStorage.removeItem('guestCredentials');
      sessionStorage.removeItem('isGuestMode');
      setGuestCredentialsState(null);
      setCoursesMode('demo');

      setPendingUserSelection(false);

      setDemoCourses(MOCK_COURSES);
      setDemoCourseDetails(MOCK_COURSE_DETAILS);

      console.log('✅ Demo mode activated successfully', {
        courses: MOCK_COURSES.length,
        user: MOCK_USER.name,
      });

      return true;
    } catch (error) {
      console.error('❌ Error activating demo mode:', error);
      return false;
    }
  }, []);

  const deactivateDemoMode = useCallback(() => {
    console.log('🎮 Deactivating demo mode...');

    sessionStorage.removeItem(DEMO_MODE_KEY);
    setCoursesMode('auth');

    setDemoCourses([]);
    setDemoCourseDetails({});

    setPendingUserSelection(true);

    console.log('✅ Demo mode deactivated');
  }, []);

  /**
   * Enter offline mode with a chosen child and snapshot.
   *
   * Synchronous and non-throwing: it is reached from a screen the user opened
   * *because* other things are failing, so it reports a boolean rather than
   * adding another error path to handle.
   */
  const activateOfflineMode = useCallback((canvasUserId: string, snapshot: any) => {
    if (!canvasUserId || !snapshot) {
      console.error('Offline mode needs both a Canvas user and a snapshot');
      return false;
    }

    try {
      sessionStorage.setItem(OFFLINE_MODE_KEY, 'true');
      sessionStorage.setItem(OFFLINE_USER_KEY, String(canvasUserId));
      sessionStorage.setItem(OFFLINE_SNAPSHOT_KEY, JSON.stringify(snapshot));
      // Task 6.0ag: who entered offline mode, for the same-account check when
      // the session ends and they sign in again. `userEmail` is gone by then
      // (clearTokens removes it). Not overwritten when absent, so switching
      // child after the session ended keeps the original owner.
      const owner = localStorage.getItem('userEmail');
      if (owner) sessionStorage.setItem(OFFLINE_OWNER_KEY, owner);
    } catch (error) {
      // A snapshot too large for sessionStorage still works for this session;
      // it just will not survive a reload. Better than refusing to start.
      console.warn('Could not persist offline snapshot across reloads:', error);
    }

    sessionStorage.removeItem(DEMO_MODE_KEY);
    setOfflineCanvasUserId(String(canvasUserId));
    setOfflineSnapshot(snapshot);
    setCoursesMode('offline');
    setPendingUserSelection(false);

    return true;
  }, []);

  // Task 6.0s. Lives here rather than in useUserSwitching because that hook
  // switches credentials, which offline mode has none of; what changes here is
  // which stored snapshot is on screen. Nearest-in-time rather than same-time:
  // see selectNearestSnapshot for why an exact match would often find nothing.
  const switchOfflineChild = useCallback(async (canvasUserId: string): Promise<OfflineSwitchResult> => {
    const target = String(canvasUserId || '').trim();
    if (!target) {
      return { ok: false, reason: 'No Canvas user id to switch to.' };
    }
    if (target === offlineCanvasUserId) {
      return { ok: true, snapshot: offlineSnapshot };
    }

    let found: any[];
    try {
      // Same defensive shape check as OfflineMode.jsx: the service returns an
      // array today, and a wrapped `{snapshots}` would otherwise read as none.
      const response: any = await getSnapshots(target);
      found = Array.isArray(response) ? response : (response?.snapshots || []);
    } catch (error) {
      console.error('Could not load snapshots for offline switch:', error);
      return { ok: false, reason: "Could not load that child's snapshots." };
    }

    // Task 6.0ad: `found` is a list of summaries (no `courses`) now that
    // GetSnapshots trims the response. selectNearestSnapshot only needs
    // `date` to pick one, but the picked entry itself cannot go to
    // activateOfflineMode - it has no course/assignment data - so its body
    // is fetched by id before switching.
    const picked = selectNearestSnapshot(found, offlineSnapshot?.date);
    if (!picked) {
      return { ok: false, reason: 'No snapshots have been imported for that child yet.' };
    }

    let snapshot: any;
    try {
      snapshot = await getSnapshot(target, picked.id);
    } catch (error) {
      console.error('Could not load snapshot body for offline switch:', error);
      return { ok: false, reason: "Could not load that child's snapshots." };
    }

    return activateOfflineMode(target, snapshot)
      ? { ok: true, snapshot }
      : { ok: false, reason: 'Could not switch offline mode to that child.' };
  }, [offlineCanvasUserId, offlineSnapshot, activateOfflineMode]);

  const deactivateOfflineMode = useCallback(() => {
    sessionStorage.removeItem(OFFLINE_MODE_KEY);
    sessionStorage.removeItem(OFFLINE_SNAPSHOT_KEY);
    sessionStorage.removeItem(OFFLINE_USER_KEY);
    sessionStorage.removeItem(OFFLINE_OWNER_KEY);

    setOfflineSnapshot(null);
    setOfflineCanvasUserId(null);
    setCoursesMode('auth');
    setPendingUserSelection(true);
  }, []);

  // Task 6.0ag. A new sign-in as a different account has just had the offline
  // session's storage removed by AuthContext (before it set the new user);
  // bring the in-memory mode into line in the same update.
  useEffect(() => {
    window.addEventListener(OFFLINE_DISCARDED_EVENT, deactivateOfflineMode);
    return () => window.removeEventListener(OFFLINE_DISCARDED_EVENT, deactivateOfflineMode);
  }, [deactivateOfflineMode]);

  const value = useMemo(() => ({
    isGuestMode,
    isDemoMode,
    isOfflineMode,
    guestCredentials,
    guestAvailableUsers,
    pendingUserSelection,
    initializing,
    coursesMode,
    dashboardPath,
    demoCourses,
    demoCourseDetails,
    offlineCourses,
    offlineCourseDetails,
    offlineSnapshot,
    offlineCanvasUserId,
    getActiveCredentials,
    setGuestCredentials,
    clearGuestCredentials,
    resetGuestSelection,
    activateDemoMode,
    deactivateDemoMode,
    activateOfflineMode,
    deactivateOfflineMode,
    switchOfflineChild,
  }), [
    isGuestMode,
    isDemoMode,
    isOfflineMode,
    guestCredentials,
    guestAvailableUsers,
    pendingUserSelection,
    initializing,
    coursesMode,
    dashboardPath,
    demoCourses,
    demoCourseDetails,
    offlineCourses,
    offlineCourseDetails,
    offlineSnapshot,
    offlineCanvasUserId,
    getActiveCredentials,
    setGuestCredentials,
    clearGuestCredentials,
    resetGuestSelection,
    activateDemoMode,
    deactivateDemoMode,
    activateOfflineMode,
    deactivateOfflineMode,
    switchOfflineChild,
  ]);

  return <AppModeContext.Provider value={value}>{children}</AppModeContext.Provider>;
};

export default AppModeContext;
